import React, { useEffect, useState } from "react";
import { Button, Flex, InputNumber, Typography, message } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

const { Text } = Typography;

/**
 * Allowed order statuses for editing manual extra charges.
 * Mirrors `Retention::shouldRender()` in Ced_CsOrder.
 */
const EDITABLE_STATUSES = new Set(["pending_payment", "payment_review"]);

interface Props {
  orderId: number;
  orderStatus?: string | null;
  currentValue?: number | null;
  onSaved?: () => void;
}

/**
 * Manual extra charges input — wraps the
 * `PATCH /v1/orders/:orderId/additional-payment` endpoint.
 *
 * Modeled after the Magento vendor panel's "Perceptions and retentions"
 * field. Visible only while the order is in pending_payment or
 * payment_review. The current value can be modified or cleared.
 */
export const ManualExtraCharges: React.FC<Props> = ({
  orderId,
  orderStatus,
  currentValue,
  onSaved,
}) => {
  const initial = currentValue && currentValue > 0 ? currentValue : null;
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState<number | null>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset local draft if the underlying value changes (e.g. after refresh).
    setDraftValue(initial);
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  const canEdit = !!orderStatus && EDITABLE_STATUSES.has(orderStatus);
  if (!canEdit && !initial) {
    // Nothing to show: no existing charge AND status doesn't allow new ones.
    return null;
  }

  const persist = async (amount: number) => {
    setSaving(true);
    try {
      await axiosInstance.patch(
        `${API_BASE_URL}/orders/${orderId}/additional-payment`,
        { amount },
      );
      onSaved?.();
      setEditing(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al guardar el cargo extra";
      message.error(Array.isArray(msg) ? msg.join(" • ") : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = () => {
    const sanitized =
      draftValue == null || !Number.isFinite(draftValue) || draftValue < 0
        ? 0
        : Number(draftValue);
    if (sanitized === (initial ?? 0)) {
      setEditing(false);
      return;
    }
    persist(sanitized);
  };

  const handleCancel = () => {
    setDraftValue(initial);
    setEditing(false);
  };

  const showInput = editing || !initial;

  return (
    <Flex vertical gap={4} style={{ marginTop: 12 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Manual extra charges
      </Text>
      <Flex gap={8} align="center">
        {showInput ? (
          <>
            <InputNumber
              value={draftValue ?? undefined}
              onChange={(v) =>
                setDraftValue(typeof v === "number" ? v : v ? Number(v) : null)
              }
              prefix="$"
              min={0}
              step={0.01}
              precision={2}
              disabled={!canEdit || saving}
              style={{ width: 200 }}
              placeholder=""
              onPressEnter={handleConfirm}
            />
            <Button
              type="text"
              icon={<CheckOutlined style={{ color: "#15744B" }} />} iconPosition="end"
              onClick={handleConfirm}
              disabled={!canEdit || saving}
              loading={saving}
            />
            {initial !== null && (
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => persist(0)}
                disabled={!canEdit || saving}
              />
            )}
            {editing && (
              <Button
                type="text"
                size="small"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </Button>
            )}
          </>
        ) : (
          <>
            <Text style={{ fontSize: 14 }}>
              {new Intl.NumberFormat("es-AR", {
                style: "currency",
                currency: "ARS",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(initial as number)}
            </Text>
            {canEdit && (
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(true)}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => persist(0)}
                  disabled={saving}
                />
              </>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
